import type { ConversationDebugPayload } from '@/lib/conversationDebug.ts';
import { DebugRow, DebugSection } from './DebugSection.tsx';
import { formatMessageDateTime } from '@/utils/messageUtils.ts';

/** When the conversation ran, and what it is made of. */
export function TimingSection({ data }: { data: ConversationDebugPayload }) {
  return (
    <DebugSection title='Timing & stats'>
      <dl>
        <DebugRow label='Created'>
          {formatMessageDateTime(data.conversation.created_at)}
        </DebugRow>
        <DebugRow label='First message'>
          {formatMessageDateTime(data.stats.first_message_at)}
        </DebugRow>
        <DebugRow label='Last message'>
          {formatMessageDateTime(data.stats.last_message_at)}
        </DebugRow>
        <DebugRow label='Duration'>{durationText(data.stats.duration_ms)}</DebugRow>
        <DebugRow label='Total messages'>{data.stats.total}</DebugRow>
        {Object.entries(data.stats.by_type).map(([type, count]) => (
          <DebugRow key={type} label={`· ${type}`}>
            {count}
          </DebugRow>
        ))}
        <DebugRow label='Languages'>
          {data.stats.distinct_languages.join(', ') || '—'}
        </DebugRow>
        <DebugRow label='Skills touched'>{data.stats.distinct_skill_ids.length}</DebugRow>
        <DebugRow label='Content filtered'>{data.stats.content_filtered_count}</DebugRow>
        <DebugRow label='Failed deliveries'>{data.stats.failed_delivery_count}</DebugRow>
      </dl>
    </DebugSection>
  );
}

/** How long the conversation ran, in the largest unit that still says something. */
function durationText(ms: number | null): string {
  if (!ms || ms <= 0) return '—';

  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}
